import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PDFDocument, rgb } from 'pdf-lib';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getQuotationDocuments, getFileContent } from 'app/entities/file/file.reducer';
import { getEntity } from 'app/entities/quotation/quotation.reducer';
import { QuotationPrint } from 'app/entities/quotation/bought/components/quotation-print';
import { QuotationPrint as BoubyanQuotationPrint } from 'app/entities/quotation/client-docs/boubyan-bank/quotation/quotation-print';
import { InvoicePrint } from 'app/entities/quotation/invoice-print';
import { InvoicePrint as BoubyanInvoicePrint } from 'app/entities/quotation/client-docs/boubyan-bank/invoice/invoice-print';
import {PdfConcatHeader} from 'app/entities/quotation/bought/components/pdf-concat-header';
import {translate} from 'react-jhipster';
import {IQuotation} from "app/shared/model/quotation.model";
import {IFileMetadata} from "app/shared/model/file.model";
import {getEntityByQuotationId} from "app/entities/quotation/iban-reducer";
import {IIban} from "app/shared/model/iban.model";
import {LocalFormPrint} from "app/entities/quotation/local-form-print";
import {PoaFormPrint} from "app/entities/quotation/poa-form-print";
import {isBoubyanQuotation} from "app/shared/util/quotation-utils";

interface UploadedPDF {
  orderNo: number;
  file: File;
}

export const PrintConcat: React.FC = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const dispatch = useAppDispatch();

  const bankStaffAccount = useAppSelector(state => state.userManagement.bankStaff);
  const hasFetched = useRef(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const quotationLoading = useAppSelector(state => state.quotation.loading);
  const ibanLoading = useAppSelector(state => state.iban.loading);

  useEffect(() => {
    const fetchData = async () => {
      if (!quotationId || hasFetched.current) return;

      try {
        const [entityResponse, filesResponse, ibanResponse] = await Promise.all([
          dispatch(getEntity(quotationId)).unwrap(),
          dispatch(getQuotationDocuments(quotationId)).unwrap(),
          dispatch(getEntityByQuotationId(quotationId)).unwrap(),
        ]);

        const quotation = entityResponse.data;
        const files = Array.isArray(filesResponse.data) ? filesResponse.data : [];
        const iban = ibanResponse.data;

        if (quotation?.id && Array.isArray(files)) {
          await generatePDF(quotation, files, iban);
        }
        hasFetched.current = true;
      } catch (error) {
        console.error('Error fetching data:', error);
        hasFetched.current = true;
      }
    };

    fetchData();
  }, [quotationId, dispatch]);

  const generatePDF = async (quotationEntity: IQuotation, files: IFileMetadata[], ibanEntity: IIban) => {
    if (!quotationEntity?.id || !Array.isArray(files)) {
      return;
    }
    setIsGenerating(true);

    try {
      // Convert uploaded files to File objects
      const uploadedPDFs: UploadedPDF[] = await Promise.all(
        files.map(async (file: any) => {
          try {
            const result: any = await dispatch(getFileContent(file?.id)).unwrap();
            const blob = new Blob([result.data], { type: 'application/pdf' });
            const pdfFile = new File([blob], file?.fileName, { type: 'application/pdf' });

            return {
              orderNo: file.orderIndex || 0,
              file: pdfFile,
            };
          } catch (error) {
            console.error(`Error fetching file ${file?.id}:`, error);
            return null;
          }
        })
      );

      // Filter out failed downloads
      const validUploadedPDFs = uploadedPDFs.filter(pdf => pdf !== null);

      // Generate PDF from components
      await generateCombinedPDF(validUploadedPDFs, quotationEntity, ibanEntity);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCombinedPDF = async (uploadedPDFs: UploadedPDF[], quotationEntity: IQuotation, ibanEntity: IIban) => {
    try {
      // Create temporary container for components
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      document.body.appendChild(tempContainer);

      // Create component elements
      const componentElements: HTMLDivElement[] = [];
      const ReactDOM = await import('react-dom');

      // Render PDF header
      const headerDiv = document.createElement('div');
      headerDiv.style.width = '210mm';
      headerDiv.style.minHeight = '297mm';
      headerDiv.style.backgroundColor = 'white';
      headerDiv.style.fontFamily = 'Arial, sans-serif';
      headerDiv.style.display = 'flex';
      headerDiv.style.justifyContent = 'center'; // Centers horizontally
      headerDiv.style.alignItems = 'center'; // Centers vertically (changed from flex-start)
      headerDiv.style.padding = '0';
      headerDiv.style.margin = '0 auto';

      tempContainer.appendChild(headerDiv);
      ReactDOM.render(<PdfConcatHeader transactionRefNo={quotationEntity?.transactionRefNo} />, headerDiv);
      componentElements.push(headerDiv);

      // Render QuotationPrint (only if quotationEnabled is true)
      if (quotationEntity?.bank?.quotationEnabled) {
        const quotationDiv = document.createElement('div');
        quotationDiv.style.width = '210mm';
        quotationDiv.style.minHeight = '297mm';
        quotationDiv.style.padding = '20mm';
        quotationDiv.style.backgroundColor = 'white';
        quotationDiv.style.fontFamily = 'Arial, sans-serif';

        quotationDiv.style.display = 'flex';
        quotationDiv.style.justifyContent = 'center';
        quotationDiv.style.alignItems = 'flex-start';
        quotationDiv.style.padding = '0';
        quotationDiv.style.margin = '0 auto';

        tempContainer.appendChild(quotationDiv);
        ReactDOM.render(
          isBoubyanQuotation(quotationEntity)
            ? <BoubyanQuotationPrint quotationEntity={quotationEntity} bankStaffAccount={bankStaffAccount} hidden={false} />
            : <QuotationPrint quotationEntity={quotationEntity} bankStaffAccount={bankStaffAccount} hidden={false} />,
          quotationDiv
        );
        componentElements.push(quotationDiv);

        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Render InvoicePrint (only if invoiceEnabled is true)
      if (quotationEntity?.bank?.invoiceEnabled) {
        const invoiceDiv = document.createElement('div');
        invoiceDiv.style.width = 'auto'; // Let it be responsive
        invoiceDiv.style.maxWidth = '210mm'; // Set max width instead
        invoiceDiv.style.minHeight = '297mm';
        invoiceDiv.style.padding = '10mm'; // Reduce padding to give more space
        invoiceDiv.style.backgroundColor = 'white';
        invoiceDiv.style.fontFamily = 'Arial, sans-serif';
        invoiceDiv.style.boxSizing = 'border-box';
        invoiceDiv.style.overflow = 'visible'; // Allow content to flow naturally

        invoiceDiv.style.display = 'flex';
        invoiceDiv.style.justifyContent = 'center';
        invoiceDiv.style.alignItems = 'flex-start';
        invoiceDiv.style.padding = '0';
        invoiceDiv.style.margin = '0 auto';

        tempContainer.appendChild(invoiceDiv);
        ReactDOM.render(
          isBoubyanQuotation(quotationEntity)
            ? <BoubyanInvoicePrint quotationEntity={quotationEntity} />
            : <InvoicePrint quotationEntity={quotationEntity} />,
          invoiceDiv
        );
        componentElements.push(invoiceDiv);

        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Render LocalFormPrint (only if localFormEnabled is true)
      if (quotationEntity?.bank?.localFormEnabled) {
        const localFormDiv = document.createElement('div');
        localFormDiv.style.width = 'auto'; // Let it be responsive
        localFormDiv.style.maxWidth = '210mm'; // Set max width instead
        localFormDiv.style.minHeight = '297mm';
        localFormDiv.style.padding = '10mm'; // Reduce padding to give more space
        localFormDiv.style.backgroundColor = 'white';
        localFormDiv.style.fontFamily = 'Arial, sans-serif';
        localFormDiv.style.boxSizing = 'border-box';
        localFormDiv.style.overflow = 'visible'; // Allow content to flow naturally

        localFormDiv.style.display = 'flex';
        localFormDiv.style.justifyContent = 'center';
        localFormDiv.style.alignItems = 'flex-start';
        localFormDiv.style.padding = '0';
        localFormDiv.style.margin = '0 auto';

        tempContainer.appendChild(localFormDiv);
        ReactDOM.render(
          <LocalFormPrint
            quotationEntity={quotationEntity}
            ibanEntity={ibanEntity}
            quotationLoading={quotationLoading}
            ibanLoading={ibanLoading}
          />,
          localFormDiv
        );
        componentElements.push(localFormDiv);

        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      if (quotationEntity?.bank?.poaFormEnabled) {
        const poaFormDiv = document.createElement('div');
        poaFormDiv.style.width = 'auto'; // Let it be responsive
        poaFormDiv.style.maxWidth = '210mm'; // Set max width instead
        poaFormDiv.style.minHeight = '297mm';
        poaFormDiv.style.padding = '10mm'; // Reduce padding to give more space
        poaFormDiv.style.backgroundColor = 'white';
        poaFormDiv.style.fontFamily = 'Arial, sans-serif';
        poaFormDiv.style.boxSizing = 'border-box';
        poaFormDiv.style.overflow = 'visible'; // Allow content to flow naturally

        poaFormDiv.style.display = 'flex';
        poaFormDiv.style.justifyContent = 'center';
        poaFormDiv.style.alignItems = 'flex-start';
        poaFormDiv.style.padding = '0';
        poaFormDiv.style.margin = '0 auto';

        tempContainer.appendChild(poaFormDiv);
        ReactDOM.render(<PoaFormPrint quotationEntity={quotationEntity} ibanEntity={ibanEntity} showPageNumber={false} />, poaFormDiv);
        componentElements.push(poaFormDiv);

        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Generate PDFs from components
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).jsPDF;

      const mergedPdf = await PDFDocument.create();

      // Convert each component to PDF and add to merged document
      for (const element of componentElements) {
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });

        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);

        const pdfBytes = new Uint8Array(pdf.output('arraybuffer'));
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
      }

      // Add uploaded PDFs
      const sortedUploadedPDFs = [...uploadedPDFs].sort((a, b) => a.orderNo - b.orderNo);
      for (const pdfFile of sortedUploadedPDFs) {
        try {
          const pdfBytes = await pdfFile.file.arrayBuffer();
          const uploadedPdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(uploadedPdfDoc, uploadedPdfDoc.getPageIndices());
          pages.forEach(page => mergedPdf.addPage(page));
        } catch (error) {
          console.error(`Error processing file ${pdfFile.file.name}: ${error.message}`);
          const blankPage = mergedPdf.addPage([595.28, 841.89]); // A4
          blankPage.drawText(`FAILED TO LOAD: ${pdfFile.file.name}`, {
            x: 50,
            y: 800,
            size: 14,
            color: rgb(1, 0, 0),
          });
        }
      }

      const customTitle = quotationEntity?.transactionRefNo ?? 'Document';
      mergedPdf.setTitle(customTitle);
      mergedPdf.setSubject('Generated PDF');

      // Generate final PDF
      const finalPdfBytes = await mergedPdf.save();
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setPdfUrl(url);

      // Auto-download PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `${customTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      componentElements.forEach(element => {
        ReactDOM.unmountComponentAtNode(element);
      });
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error('Error generating combined PDF:', error);
    }
  };

  if (isGenerating) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <div style={{ fontSize: '18px', marginBottom: '20px' }}>{translate('comexApp.quotation.generatingPdf')}</div>
        <div
          style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 2s linear infinite',
          }}
        />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (pdfUrl) {
    return (
      <div style={{ width: '100vw', height: '100vh' }}>
        <iframe
          src={pdfUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          title="Combined PDF Document"
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <div>{translate('comexApp.quotation.loadingQuotation')}</div>
    </div>
  );
};
